import { useEffect, useMemo, useRef, useState } from 'react'
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom'
import { COMPANY_NAME, greetingName, randomCatchphraseKey, timeGreetingKey, useT, userDisplayName } from '@reciperoll/core'
import { Menu, Search } from 'react-feather'
import SidebarContent from './SidebarContent'
import { useAuth } from '../lib/auth'

export default function AppLayout() {
  const { session } = useAuth()
  const email = session?.user.email ?? undefined
  // The editable display name lives on the auth user's metadata under
  // `display_name` (see userDisplayName); fall back to the email.
  const fullName = userDisplayName(session?.user.user_metadata)

  const t = useT()
  // Shared home-header greeting (same util as the RN app).
  const greeting = t(timeGreetingKey())
  const rawName = greetingName(fullName, email)
  const firstName = rawName === 'there' ? t('home.there') : rawName
  const catchphrase = t(useMemo(() => randomCatchphraseKey(), []))
  const navigate = useNavigate()
  const location = useLocation()
  const [search, setSearch] = useState('')
  const searchRef = useRef<HTMLInputElement>(null)
  const [menuOpen, setMenuOpen] = useState(false)

  // Press "/" (when not already typing in a field) to jump to the search box;
  // Escape closes the mobile drawer.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setMenuOpen(false)
        return
      }
      if (e.key !== '/' || e.metaKey || e.ctrlKey || e.altKey) return
      const el = document.activeElement as HTMLElement | null
      const tag = el?.tagName
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || el?.isContentEditable) return
      e.preventDefault()
      searchRef.current?.focus()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  // Close the mobile drawer whenever the route changes (link tap / search).
  useEffect(() => {
    setMenuOpen(false)
  }, [location.pathname])

  // Lock background scroll while the mobile drawer is open.
  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : ''
    return () => {
      document.body.style.overflow = ''
    }
  }, [menuOpen])

  return (
    <div className="min-h-screen flex">
      <aside className="hidden md:flex md:w-[220px] lg:w-[252px] shrink-0 flex-col border-r border-line bg-chrome h-screen sticky top-0">
        <SidebarContent />
      </aside>

      {/* Mobile navigation drawer (slides in from the left). */}
      <div
        className={`md:hidden fixed inset-0 z-50 overflow-hidden ${menuOpen ? '' : 'pointer-events-none'}`}
        aria-hidden={!menuOpen}
      >
        <div
          onClick={() => setMenuOpen(false)}
          className={`absolute inset-0 bg-black/40 transition-opacity duration-200 ${menuOpen ? 'opacity-100' : 'opacity-0'}`}
        />
        <div
          className={`absolute inset-y-0 left-0 w-[280px] max-w-[82%] bg-chrome flex flex-col shadow-xl transition-transform duration-200 ${menuOpen ? 'translate-x-0' : '-translate-x-full'}`}
        >
          <SidebarContent />
        </div>
      </div>

      <main className="flex-1 min-w-0 flex flex-col">
        <header className="sticky top-0 z-10 bg-chrome/85 backdrop-blur-md border-b border-line px-4 sm:px-6 lg:px-10 py-4 sm:py-5">
          <div className="mx-auto max-w-5xl w-full flex flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
              <button
                type="button"
                aria-label={t('home.openMenu')}
                onClick={() => setMenuOpen(true)}
                className="md:hidden shrink-0 -ml-1.5 w-10 h-10 flex items-center justify-center rounded-xl text-ink-soft hover:bg-surface-2 cursor-pointer"
              >
                <Menu className="w-6 h-6" />
              </button>
              <div className="min-w-0">
                <p className="text-sm text-ink-soft font-medium truncate">{t('home.greeting', { greeting, name: firstName })}</p>
                <h1 className="font-display text-xl sm:text-2xl font-semibold text-ink truncate">{catchphrase}</h1>
              </div>
            </div>

            {/* Full search box on tablet/desktop */}
            <div className="hidden sm:flex shrink-0 items-center bg-surface border border-line rounded-xl px-4 py-2.5 gap-2.5 w-[280px] focus-within:border-terracotta transition-colors">
              <Search className="w-4 h-4 text-ink-faint shrink-0" />
              <input
                ref={searchRef}
                type="text"
                placeholder={t('home.searchRecipes')}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && search.trim()) {
                    navigate(`/search?query=${encodeURIComponent(search.trim())}`)
                    setSearch('')
                  }
                }}
                className="bg-transparent text-sm outline-none w-full placeholder:text-ink-faint"
              />
              <kbd className="hidden lg:inline text-[10px] font-mono text-ink-faint bg-surface-2 rounded px-1.5 py-0.5">/</kbd>
            </div>

            {/* Compact search button on phones (→ full search screen) */}
            <Link
              to="/search"
              aria-label={t('common.search')}
              className="sm:hidden shrink-0 w-10 h-10 flex items-center justify-center rounded-xl bg-surface border border-line text-ink-soft hover:text-ink"
            >
              <Search className="w-5 h-5" />
            </Link>
          </div>
        </header>

        <div className="flex-1 px-4 sm:px-6 lg:px-10 py-8 overflow-x-clip">
          <Outlet />
        </div>

        <footer className="px-4 sm:px-6 lg:px-10 pb-6 text-center">
          <p className="text-xs text-ink-faint">
            {t('account.copyright', { year: new Date().getFullYear(), company: COMPANY_NAME })}
          </p>
        </footer>
      </main>
    </div>
  )
}
